import type {SessionCampaignPayload} from '../types/LoggingTypes'

const CAMPAIGN_PAYLOAD_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'campaign_id',
  'adgroup_id',
  'ad_id',
  'device',
  'network',
  'matchtype',
] as const

const CAMPAIGN_MARKER_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'campaign_id',
  'adgroup_id',
  'ad_id',
  'network',
  'matchtype',
] as const

const isCampaignLink = (searchParams: URLSearchParams): boolean => {
  return CAMPAIGN_MARKER_KEYS.some((key) => searchParams.has(key))
}

const getEmptyCampaignPayload = (): SessionCampaignPayload => {
  const payload: SessionCampaignPayload = {}

  CAMPAIGN_PAYLOAD_KEYS.forEach((key) => {
    const prefixedKey =
      `Session_campaign_payload_${key}` as keyof SessionCampaignPayload
    payload[prefixedKey] = ''
  })

  return payload
}

const extractCampaignPayload = (
  searchParams: URLSearchParams
): SessionCampaignPayload | undefined => {
  if (!isCampaignLink(searchParams)) return undefined

  const payload: SessionCampaignPayload = {}

  CAMPAIGN_PAYLOAD_KEYS.forEach((key) => {
    const value = searchParams.get(key)

    if (value !== null) {
      const prefixedKey =
        `Session_campaign_payload_${key}` as keyof SessionCampaignPayload
      payload[prefixedKey] = value
    }
  })

  return payload
}

const extractCampaignPayloadFromSessionCampaign = (
  cookieValue?: string
): SessionCampaignPayload | undefined => {
  if (!cookieValue) return undefined

  try {
    const sessionCampaign = JSON.parse(cookieValue) as {
      values?: Record<string, unknown>
    }
    const values = sessionCampaign?.values

    if (!values || typeof values !== 'object') return undefined

    const payload: SessionCampaignPayload = {}
    let hasCampaignValue = false

    CAMPAIGN_PAYLOAD_KEYS.forEach((key) => {
      const value = values[key]

      if (typeof value === 'string') {
        const prefixedKey =
          `Session_campaign_payload_${key}` as keyof SessionCampaignPayload
        payload[prefixedKey] = value
        hasCampaignValue = true
      }
    })

    return hasCampaignValue ? payload : undefined
  } catch {
    return undefined
  }
}

// Back to the raw url names the campaign arrived with, so the add post app reads the same
// keys it would have seen on the landing url. Empty values are skipped.
const toCampaignQueryEntries = (payload?: SessionCampaignPayload): [string, string][] => {
  if (!payload) return []

  const entries: [string, string][] = []

  CAMPAIGN_PAYLOAD_KEYS.forEach((key) => {
    const value = payload[`Session_campaign_payload_${key}`]

    if (value) {
      entries.push([key, value])
    }
  })

  return entries
}

const toCampaignQueryParams = (payload?: SessionCampaignPayload): string[] =>
  toCampaignQueryEntries(payload).map(([key, value]) => `${key}=${encodeURIComponent(value)}`)

export {
  CAMPAIGN_PAYLOAD_KEYS,
  extractCampaignPayload,
  extractCampaignPayloadFromSessionCampaign,
  getEmptyCampaignPayload,
  toCampaignQueryEntries,
  toCampaignQueryParams,
}
